import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trash2, User, Mail, Calendar, ShieldCheck, Loader2, Users } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";
import API_URL from "../../config";


const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Deletion Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/user/all-users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch user directory.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/user/delete-account/${userToDelete._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`${userToDelete.name}'s account has been permanently removed.`);
            setUsers(users.filter(u => u._id !== userToDelete._id));
        } catch (err) {
            console.error(err);
            toast.error("Security protocols blocked the deletion of this account.");
        } finally {
            setUserToDelete(null);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-96 gap-4">
            <Loader2 className="animate-spin text-sky-600" size={48} />
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-900/40">Synchronizing User Identities...</p>
        </div>
    );

    return (
        <div className="space-y-10 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-sky-50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                <div className="relative z-10">
                    <h2 className="text-3xl font-black text-sky-950 flex items-center gap-3 tracking-tight">
                        <ShieldCheck className="text-sky-500" size={32} />
                        Authorized Users
                    </h2>
                    <p className="text-gray-400 font-bold text-sm mt-1">Full administrative control over identity modules and access levels.</p>
                </div>
                <div className="mt-6 md:mt-0 bg-sky-50 px-6 py-4 rounded-[1.5rem] border border-sky-100 flex items-center gap-4 shadow-inner relative z-10">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-sky-600 shadow-sm">
                        <Users size={20} />
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-sky-900/40 uppercase tracking-widest">Global Reach</p>
                        <h4 className="text-xl font-black text-sky-900 leading-none">{users.length} Identities</h4>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Security Identity</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Access Protocol</th>
                                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Identity Revocation</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {users.map((u) => (
                                <tr key={u._id} className="hover:bg-sky-50/20 transition-all duration-300 group">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center text-sky-600 border border-sky-100 group-hover:bg-sky-100 transition-colors shadow-inner">
                                                <User size={22} />
                                            </div>
                                            <div>
                                                <div className="font-black text-sky-950 text-base tracking-tight">{u.name}</div>
                                                <div className="text-[11px] text-gray-400 font-bold flex items-center gap-1.5 mt-0.5 lowercase tracking-tight">
                                                    <Mail size={12} className="text-sky-300" /> {u.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col gap-1.5">
                                            <span className={`inline-flex px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest w-max shadow-sm ${u.role === 'admin'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-emerald-500 text-white'
                                                }`}>
                                                {u.role} Access
                                            </span>
                                            <div className="text-[10px] text-gray-400 font-bold flex items-center gap-1.5">
                                                <Calendar size={12} className="text-sky-300" />
                                                Onboard {new Date(u.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-center">
                                        {u.role !== 'admin' ? (
                                            <button
                                                onClick={() => handleDeleteClick(u)}
                                                className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-100 transition-all flex items-center justify-center mx-auto active:scale-95 group/del"
                                                title="Revoke Identity"
                                            >
                                                <Trash2 size={20} className="group-hover/del:scale-110 transition-transform" />
                                            </button>
                                        ) : (
                                            <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-300 mx-auto cursor-not-allowed border border-dashed border-gray-200" title="Security Clearance Locked">
                                                <ShieldCheck size={20} />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {users.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-24 bg-gray-50/30 gap-6">
                        <div className="w-20 h-20 bg-white rounded-full shadow-inner flex items-center justify-center text-gray-200">
                            <Users size={40} />
                        </div>
                        <div className="text-center">
                            <h3 className="text-xl font-black text-sky-950">Identity Registry Empty</h3>
                            <p className="text-xs text-gray-400 font-bold mt-2">No synchronized human assets detected in this environment.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Revocation Modal */}
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Revoke Human Identity"
                message={`This will permanently purge "${userToDelete?.name}'s" security clearance and destroy all associated session logs. This action is terminal and irreversible. Complete revocation?`}
                confirmText="Terminate Account"
            />
        </div>
    );
};

export default ManageUsers;
